import React, { useState, useEffect } from 'react';
import { apiClient } from './services/api';
import './RoomsManagement.css';

// Helper: Resim URL'ini düzelt
const getImageUrl = (url: string): string => {
  if (url.startsWith('http')) return url; // External URL
  // Backend uploads nginx üzerinden proxy ediliyor
  return url; // /uploads/rooms/xxx.jpg -> nginx proxy ile backend'e gider
};

interface Room {
  id: string;
  asistpro_room_id: string;
  room_number: string;
  room_type_name: string;
  description: string;
  floor: number;
  view_type: string;
  max_occupancy: number;
  max_adults: number;
  max_children: number;
  base_price: string;
  amenities: string[];
  images: RoomImage[];
  custom_features: string[];
  is_active: boolean;
  display_on_website: boolean;
  sort_order: number;
  synced_at: string;
}

interface RoomImage {
  url: string;
  caption: string;
  sort_order: number;
}

const RoomsManagement: React.FC = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Form states
  const [editDescription, setEditDescription] = useState('');
  const [editImages, setEditImages] = useState<RoomImage[]>([]);
  const [editFeatures, setEditFeatures] = useState<string[]>([]);
  const [editDisplayOnWebsite, setEditDisplayOnWebsite] = useState(true);
  const [editSortOrder, setEditSortOrder] = useState(0);
  const [newFeatureInput, setNewFeatureInput] = useState('');

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/rooms');
      setRooms(response.data.rooms || []);
    } catch (error: any) {
      console.error('Odalar yüklenemedi:', error);
      alert('Odalar yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!window.confirm('AsistPro\'dan odaları senkronize etmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setSyncing(true);
      const response = await apiClient.post('/admin/rooms/sync');
      alert(`✅ Başarılı!\n\nYeni oda: ${response.data.data.newRooms}\nGüncellenen: ${response.data.data.updatedRooms}\nToplam: ${response.data.data.totalSynced}`);
      await loadRooms();
    } catch (error: any) {
      console.error('Senkronizasyon hatası:', error);
      alert('Senkronizasyon hatası: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleEditRoom = (room: Room) => {
    setSelectedRoom(room);
    setEditDescription(room.description || '');
    setEditImages(room.images || []);
    setEditFeatures(room.custom_features || []);
    setEditDisplayOnWebsite(room.display_on_website);
    setEditSortOrder(room.sort_order || 0);
    setNewFeatureInput(''); // Input'u temizle
    setShowEditModal(true);
  };

  const handleSaveRoom = async () => {
    if (!selectedRoom) return;

    try {
      setLoading(true);
      await apiClient.put(`/admin/rooms/${selectedRoom.id}`, {
        description: editDescription,
        images: editImages,
        custom_features: editFeatures,
        display_on_website: editDisplayOnWebsite,
        sort_order: editSortOrder
      });
      
      alert('Oda başarıyla güncellendi!');
      setShowEditModal(false);
      await loadRooms();
    } catch (error: any) {
      console.error('Güncelleme hatası:', error);
      alert('Güncelleme hatası: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await apiClient.post('/upload/room-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
          const imageUrl = response.data.data.url;
          setEditImages([...editImages, { 
            url: imageUrl, 
            caption: '', 
            sort_order: editImages.length 
          }]);
          alert('✅ Resim yüklendi!');
        }
      } catch (error: any) {
        console.error('Upload error:', error);
        alert('❌ Yükleme hatası: ' + (error.response?.data?.message || error.message));
      }
    };
    input.click();
  };

  const removeImage = async (index: number) => {
    const image = editImages[index];
    
    if (!window.confirm('Bu resmi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      // Eğer resim sistemde yüklüyse backend'den sil
      if (image.url.startsWith('/uploads/')) {
        const filename = image.url.split('/').pop();
        await apiClient.delete(`/upload/room-image/${filename}`);
      }
      
      setEditImages(editImages.filter((_, i) => i !== index));
    } catch (error: any) {
      console.error('Delete error:', error);
      alert('⚠️ Resim listeden kaldırıldı ama dosya silinirken hata oluştu');
      setEditImages(editImages.filter((_, i) => i !== index));
    }
  };

  const addFeature = () => {
    if (!newFeatureInput.trim()) return;
    setEditFeatures([...editFeatures, newFeatureInput.trim()]);
    setNewFeatureInput('');
  };

  const removeFeature = (index: number) => {
    setEditFeatures(editFeatures.filter((_, i) => i !== index));
  };

  return (
    <div className="rooms-management">
      <div className="management-header">
        <h2>🏠 Odalar Yönetimi</h2>
        <button 
          className="btn-sync" 
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? '⏳ Senkronize ediliyor...' : '🔄 AsistPro\'dan Senkronize Et'}
        </button>
      </div>

      {loading && !syncing ? (
        <div className="loading">Yükleniyor...</div>
      ) : (
        <>
          <div className="rooms-stats">
            <div className="stat-card">
              <div className="stat-label">Toplam Oda</div>
              <div className="stat-value">{rooms.length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Aktif</div>
              <div className="stat-value">{rooms.filter(r => r.is_active).length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Sitede Gösterilen</div>
              <div className="stat-value">{rooms.filter(r => r.display_on_website).length}</div>
            </div>
          </div>

          <div className="rooms-grid">
            {rooms.map(room => (
              <div key={room.id} className="room-management-card">
                <div className="room-header">
                  <h3>{room.room_number}</h3>
                  <span className={`room-status ${room.display_on_website ? 'active' : 'inactive'}`}>
                    {room.display_on_website ? '✓ Sitede' : '✗ Gizli'}
                  </span>
                </div>
                
                <div className="room-info">
                  <div className="info-row">
                    <span className="label">Kat:</span>
                    <span>{room.floor || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Kapasite:</span>
                    <span>{room.max_adults} yetişkin + {room.max_children} çocuk</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Manzara:</span>
                    <span>{room.view_type || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Fiyat:</span>
                    <span>{room.base_price ? `${parseFloat(room.base_price).toLocaleString('tr-TR')} TL` : 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Resimler:</span>
                    <span>{room.images?.length || 0} adet</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Özel Özellikler:</span>
                    <span>{room.custom_features?.length || 0} adet</span>
                  </div>
                </div>

                <button className="btn-edit" onClick={() => handleEditRoom(room)}>
                  ✏️ Düzenle
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRoom && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Oda Düzenle: {selectedRoom.room_number}</h3>
              <button className="btn-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={4}
                  placeholder="Oda açıklaması..."
                />
              </div>

              <div className="form-group">
                <label>Resimler ({editImages.length})</label>
                <div className="images-list">
                  {editImages.map((img, idx) => (
                    <div key={idx} className="image-item">
                      <img src={getImageUrl(img.url)} alt="" />
                      <input
                        type="text"
                        placeholder="Açıklama"
                        value={img.caption}
                        onChange={(e) => {
                          const updated = [...editImages];
                          updated[idx].caption = e.target.value;
                          setEditImages(updated);
                        }}
                      />
                      <button onClick={() => removeImage(idx)}>🗑️</button>
                    </div>
                  ))}
                </div>
                <button className="btn-add" onClick={addImage}>+ Resim Ekle</button>
              </div>

              <div className="form-group">
                <label>Özel Özellikler ({editFeatures.length})</label>
                <div className="features-list">
                  {editFeatures.map((feature, idx) => (
                    <div key={idx} className="feature-item">
                      <span>{feature}</span>
                      <button onClick={() => removeFeature(idx)}>✕</button>
                    </div>
                  ))}
                </div>
                <div className="feature-input-group">
                  <input
                    type="text"
                    className="feature-input"
                    placeholder="Örn: Jakuzi, Balkon, Deniz Manzarası..."
                    value={newFeatureInput}
                    onChange={(e) => setNewFeatureInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addFeature();
                      }
                    }}
                  />
                  <button 
                    className="btn-add" 
                    onClick={addFeature}
                    disabled={!newFeatureInput.trim()}
                  >
                    + Ekle
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editDisplayOnWebsite}
                      onChange={(e) => setEditDisplayOnWebsite(e.target.checked)}
                    />
                    Sitede Göster
                  </label>
                </div>

                <div className="form-group">
                  <label>Sıralama</label>
                  <input
                    type="number"
                    value={editSortOrder}
                    onChange={(e) => setEditSortOrder(parseInt(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                İptal
              </button>
              <button className="btn-primary" onClick={handleSaveRoom} disabled={loading}>
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomsManagement;
